import { getPersonId } from '../analytics/person-id';
import type {
    InboundXService,
    PostRequestCaptureEvent,
    PostRequestCapturePayloadKind,
    PostRequestCaptureSource,
} from '../services/inboundx';
import { logger } from '../utils/core/logger';
import { normalizeDomain } from '../utils/domain/domain';
import { matchesUrlPattern } from '../utils/web/urlUtils';

type PostCaptureTransport = Pick<
    InboundXService,
    'capturePostRequest' | 'getApiUrl' | 'getSessionId'
>;

interface PostRequestCaptureManagerOptions {
    enabled: boolean;
    redactPayloadValues?: boolean;
    pagePatterns: string[];
    siteDomain: string;
    transport: PostCaptureTransport;
}

interface NormalizedPostRequestCaptureManagerOptions
    extends Omit<PostRequestCaptureManagerOptions, 'redactPayloadValues'> {
    redactPayloadValues: boolean;
}

interface PostRequestCaptureFileMetadata {
    fieldName?: string;
    name: string;
    size: number;
    type?: string;
    lastModified?: number;
}

interface SerializedPayload {
    payloadKind: PostRequestCapturePayloadKind;
    payloadJson?: unknown;
    payloadText?: string;
    hasFiles: boolean;
    filesMeta: PostRequestCaptureFileMetadata[];
}

interface XhrCaptureMetadata {
    method?: string;
    url?: string;
    headers: Record<string, string>;
}

const XHR_CAPTURE_METADATA = Symbol('rosePostRequestCaptureXhrMetadata');

const EMPTY_SERIALIZED_PAYLOAD: SerializedPayload = {
    payloadKind: 'empty',
    hasFiles: false,
    filesMeta: [],
};

const EMAIL_PLACEHOLDER = '[EMAIL]';
const FILE_PLACEHOLDER = '[FILE]';
const REDACTED_PLACEHOLDER = '[REDACTED]';
const TEXT_PLACEHOLDER = '[TEXT]';
const TEXT_WITH_EMAIL_PLACEHOLDER = '[TEXT+EMAIL]';
const EMAIL_PATTERN = /[A-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[A-Z0-9-]+(?:\.[A-Z0-9-]+)+/i;

function normalizeMethod(method: string | undefined | null): string {
    return (method ?? 'GET').toUpperCase();
}

function normalizeContentType(contentType: string | null | undefined): string | undefined {
    return contentType?.trim() || undefined;
}

function containsEmail(value: string): boolean {
    return EMAIL_PATTERN.test(value);
}

function isJsonContentType(contentType: string | undefined): boolean {
    return contentType?.toLowerCase().includes('application/json') === true;
}

function isFormUrlEncodedContentType(contentType: string | undefined): boolean {
    return contentType?.toLowerCase().includes('application/x-www-form-urlencoded') === true;
}

function isMultipartContentType(contentType: string | undefined): boolean {
    return contentType?.toLowerCase().includes('multipart/form-data') === true;
}

function isTextContentType(contentType: string | undefined): boolean {
    return contentType?.toLowerCase().startsWith('text/') === true;
}

function looksLikeJson(text: string): boolean {
    const trimmed = text.trim();
    return trimmed.startsWith('{') || trimmed.startsWith('[');
}

function getCurrentPageUrl(): string {
    return typeof window !== 'undefined' ? window.location.href : '';
}

function toAbsoluteUrl(url: string | URL): string {
    if (typeof window === 'undefined') {
        return typeof url === 'string' ? url : url.toString();
    }

    try {
        return new URL(url.toString(), window.location.href).href;
    } catch {
        return url.toString();
    }
}

function isSameSiteRequestUrl(requestUrl: string, siteDomain: string): boolean {
    try {
        const baseUrl = typeof window !== 'undefined' ? window.location.href : 'http://localhost';
        const requestDomain = normalizeDomain(new URL(requestUrl, baseUrl).hostname);
        return requestDomain === normalizeDomain(siteDomain);
    } catch {
        return false;
    }
}

function getHeaderValue(headers: HeadersInit | undefined, key: string): string | undefined {
    if (!headers) {
        return undefined;
    }

    const normalizedKey = key.toLowerCase();
    if (headers instanceof Headers) {
        return headers.get(key) ?? undefined;
    }

    if (Array.isArray(headers)) {
        const entry = headers.find(([name]) => name.toLowerCase() === normalizedKey);
        return entry?.[1];
    }

    for (const [name, value] of Object.entries(headers)) {
        if (name.toLowerCase() === normalizedKey && typeof value === 'string') {
            return value;
        }
    }

    return undefined;
}

function inferContentTypeFromBody(body: unknown): string | undefined {
    if (body == null) {
        return undefined;
    }

    if (typeof body === 'string') {
        return 'text/plain;charset=UTF-8';
    }

    if (typeof URLSearchParams !== 'undefined' && body instanceof URLSearchParams) {
        return 'application/x-www-form-urlencoded;charset=UTF-8';
    }

    if (typeof FormData !== 'undefined' && body instanceof FormData) {
        return 'multipart/form-data';
    }

    if (typeof Blob !== 'undefined' && body instanceof Blob && body.type) {
        return body.type;
    }

    return undefined;
}

function toFileMetadata(fieldName: string | undefined, file: File): PostRequestCaptureFileMetadata {
    return {
        fieldName,
        name: file.name,
        size: file.size,
        type: file.type || undefined,
        lastModified: file.lastModified === 0 ? 0 : file.lastModified || undefined,
    };
}

function appendNormalizedValue(target: Record<string, unknown>, key: string, value: unknown): void {
    const existingValue = target[key];
    if (existingValue === undefined) {
        target[key] = value;
        return;
    }

    if (Array.isArray(existingValue)) {
        existingValue.push(value);
        return;
    }

    target[key] = [existingValue, value];
}

function serializeFormEntries(entries: Iterable<[string, FormDataEntryValue]>): SerializedPayload {
    const payloadJson: Record<string, unknown> = {};
    const filesMeta: PostRequestCaptureFileMetadata[] = [];

    for (const [fieldName, value] of entries) {
        if (typeof File !== 'undefined' && value instanceof File) {
            const fileMetadata = toFileMetadata(fieldName, value);
            filesMeta.push(fileMetadata);
            appendNormalizedValue(payloadJson, fieldName, fileMetadata);
            continue;
        }

        appendNormalizedValue(payloadJson, fieldName, value);
    }

    return {
        payloadKind: 'form_data',
        payloadJson,
        hasFiles: filesMeta.length > 0,
        filesMeta,
    };
}

function serializeUrlSearchParams(params: URLSearchParams): SerializedPayload {
    const payloadJson: Record<string, unknown> = {};

    for (const [key, value] of params.entries()) {
        appendNormalizedValue(payloadJson, key, value);
    }

    return {
        payloadKind: 'form_urlencoded',
        payloadJson,
        hasFiles: false,
        filesMeta: [],
    };
}

function serializeTextPayload(text: string, contentType: string | undefined): SerializedPayload {
    const normalizedText = text.trim();
    if (normalizedText.length === 0) {
        return EMPTY_SERIALIZED_PAYLOAD;
    }

    if (isJsonContentType(contentType) || (!contentType && looksLikeJson(normalizedText))) {
        try {
            return {
                payloadKind: 'json',
                payloadJson: JSON.parse(normalizedText),
                hasFiles: false,
                filesMeta: [],
            };
        } catch {
            return {
                payloadKind: 'text',
                payloadText: text,
                hasFiles: false,
                filesMeta: [],
            };
        }
    }

    if (isFormUrlEncodedContentType(contentType)) {
        return serializeUrlSearchParams(new URLSearchParams(text));
    }

    return {
        payloadKind: 'text',
        payloadText: text,
        hasFiles: false,
        filesMeta: [],
    };
}

function serializeNativeFormText(formData: FormData): string {
    const lines: string[] = [];

    for (const [fieldName, value] of formData.entries()) {
        if (typeof File !== 'undefined' && value instanceof File) {
            lines.push(`${fieldName}=${JSON.stringify(toFileMetadata(fieldName, value))}`);
            continue;
        }

        lines.push(`${fieldName}=${value}`);
    }

    return lines.join('\r\n');
}

function redactPayloadText(payloadText: string | undefined): string | undefined {
    if (payloadText === undefined) {
        return undefined;
    }

    return containsEmail(payloadText) ? TEXT_WITH_EMAIL_PLACEHOLDER : TEXT_PLACEHOLDER;
}

function redactPayloadValue(value: unknown, fileMetadata: Set<object>): unknown {
    if (typeof value === 'string') {
        return containsEmail(value) ? EMAIL_PLACEHOLDER : REDACTED_PLACEHOLDER;
    }

    if (
        typeof value === 'number' ||
        typeof value === 'boolean' ||
        value === null ||
        value === undefined
    ) {
        return REDACTED_PLACEHOLDER;
    }

    if (Array.isArray(value)) {
        return value.map((entry) => redactPayloadValue(entry, fileMetadata));
    }

    if (typeof value === 'object') {
        if (fileMetadata.has(value)) {
            return FILE_PLACEHOLDER;
        }

        const redactedObject: Record<string, unknown> = {};
        for (const [key, nestedValue] of Object.entries(value)) {
            redactedObject[key] = redactPayloadValue(nestedValue, fileMetadata);
        }
        return redactedObject;
    }

    return REDACTED_PLACEHOLDER;
}

function maybeRedactSerializedPayload(
    serializedPayload: SerializedPayload,
    redactPayloadValues: boolean
): SerializedPayload {
    if (!redactPayloadValues) {
        return serializedPayload;
    }

    const fileMetadata = new Set<object>(serializedPayload.filesMeta);

    return {
        ...serializedPayload,
        payloadJson:
            serializedPayload.payloadJson === undefined
                ? undefined
                : redactPayloadValue(serializedPayload.payloadJson, fileMetadata),
        payloadText: redactPayloadText(serializedPayload.payloadText),
    };
}

function serializeFormPayload(
    formData: FormData,
    contentType: string | undefined
): SerializedPayload {
    if (isFormUrlEncodedContentType(contentType)) {
        const serialized = serializeFormEntries(formData.entries());
        return {
            ...serialized,
            payloadKind: 'form_urlencoded',
        };
    }

    if (isTextContentType(contentType)) {
        const serialized = serializeFormEntries(formData.entries());
        return {
            ...serialized,
            payloadKind: 'text',
            payloadText: serializeNativeFormText(formData),
        };
    }

    return serializeFormEntries(formData.entries());
}

async function serializeBody(
    body: BodyInit | Document | XMLHttpRequestBodyInit | null | undefined,
    contentType: string | undefined
): Promise<SerializedPayload> {
    if (body == null) {
        return EMPTY_SERIALIZED_PAYLOAD;
    }

    if (typeof body === 'string') {
        return serializeTextPayload(body, contentType);
    }

    if (typeof URLSearchParams !== 'undefined' && body instanceof URLSearchParams) {
        return serializeUrlSearchParams(body);
    }

    if (typeof FormData !== 'undefined' && body instanceof FormData) {
        return serializeFormPayload(body, contentType ?? 'multipart/form-data');
    }

    if (typeof File !== 'undefined' && body instanceof File) {
        return {
            payloadKind: 'unreadable',
            hasFiles: true,
            filesMeta: [toFileMetadata(undefined, body)],
        };
    }

    if (typeof Blob !== 'undefined' && body instanceof Blob) {
        return {
            payloadKind: 'unreadable',
            hasFiles: false,
            filesMeta: [],
        };
    }

    if (
        body instanceof ArrayBuffer ||
        ArrayBuffer.isView(body) ||
        (typeof ReadableStream !== 'undefined' && body instanceof ReadableStream)
    ) {
        return {
            payloadKind: 'unreadable',
            hasFiles: false,
            filesMeta: [],
        };
    }

    if (typeof Document !== 'undefined' && body instanceof Document) {
        return {
            payloadKind: 'unreadable',
            hasFiles: false,
            filesMeta: [],
        };
    }

    return {
        payloadKind: 'unreadable',
        hasFiles: false,
        filesMeta: [],
    };
}

async function serializeRequestClone(
    requestClone: Request,
    contentType: string | undefined
): Promise<SerializedPayload> {
    try {
        if (isMultipartContentType(contentType)) {
            const formData = await requestClone.formData();
            return serializeFormPayload(formData, contentType);
        }

        if (
            isJsonContentType(contentType) ||
            isFormUrlEncodedContentType(contentType) ||
            isTextContentType(contentType) ||
            !contentType
        ) {
            const text = await requestClone.text();
            return serializeTextPayload(text, contentType);
        }
    } catch (error) {
        logger.debug('POST capture: failed to serialize request clone', {
            error: error instanceof Error ? error.message : String(error),
            contentType,
        });
    }

    return {
        payloadKind: 'unreadable',
        hasFiles: false,
        filesMeta: [],
    };
}

function getFormMethod(form: HTMLFormElement): string {
    return normalizeMethod(form.getAttribute('method') ?? form.method ?? 'GET');
}

function getFormAction(form: HTMLFormElement): string {
    return toAbsoluteUrl(form.getAttribute('action') || form.action || getCurrentPageUrl());
}

function getFormClassName(form: HTMLFormElement): string | undefined {
    const className = form.className?.trim();
    return className || undefined;
}

function buildFormData(form: HTMLFormElement, submitter: HTMLElement | null): FormData {
    try {
        if (submitter) {
            return new FormData(form, submitter);
        }
    } catch {
        logger.debug('POST capture: falling back to FormData(form) without submitter');
    }

    return new FormData(form);
}

export class PostRequestCaptureManager {
    private options: NormalizedPostRequestCaptureManagerOptions;
    private installed = false;
    private originalFetch: typeof window.fetch | null = null;
    private originalXhrOpen: typeof XMLHttpRequest.prototype.open | null = null;
    private originalXhrSend: typeof XMLHttpRequest.prototype.send | null = null;
    private originalXhrSetRequestHeader: typeof XMLHttpRequest.prototype.setRequestHeader | null =
        null;
    private originalSendBeacon: typeof navigator.sendBeacon | null = null;
    private originalFormSubmit: typeof HTMLFormElement.prototype.submit | null = null;

    constructor(options: PostRequestCaptureManagerOptions) {
        this.options = {
            ...options,
            redactPayloadValues: options.redactPayloadValues !== false,
            pagePatterns: [...options.pagePatterns],
        };
    }

    public update(options: PostRequestCaptureManagerOptions): void {
        this.options = {
            ...options,
            redactPayloadValues: options.redactPayloadValues !== false,
            pagePatterns: [...options.pagePatterns],
        };

        if (!this.options.enabled) {
            this.stop();
            return;
        }

        if (!this.installed) {
            this.start();
        }
    }

    public start(): void {
        if (this.installed || !this.options.enabled || typeof window === 'undefined') {
            return;
        }

        this.patchFetch();
        this.patchXMLHttpRequest();
        this.patchSendBeacon();
        this.patchNativeForms();
        this.installed = true;
    }

    public stop(): void {
        if (!this.installed || typeof window === 'undefined') {
            return;
        }

        if (this.originalFetch) {
            window.fetch = this.originalFetch;
        }

        if (this.originalXhrOpen) {
            XMLHttpRequest.prototype.open = this.originalXhrOpen;
        }

        if (this.originalXhrSend) {
            XMLHttpRequest.prototype.send = this.originalXhrSend;
        }

        if (this.originalXhrSetRequestHeader) {
            XMLHttpRequest.prototype.setRequestHeader = this.originalXhrSetRequestHeader;
        }

        if (this.originalSendBeacon && typeof navigator !== 'undefined') {
            navigator.sendBeacon = this.originalSendBeacon;
        }

        if (this.originalFormSubmit) {
            HTMLFormElement.prototype.submit = this.originalFormSubmit;
        }

        document.removeEventListener('submit', this.handleSubmit, true);
        this.installed = false;
    }

    public isInstalled(): boolean {
        return this.installed;
    }

    private isPageMatched(pageUrl: string): boolean {
        if (!this.options.enabled || this.options.pagePatterns.length === 0) {
            return false;
        }

        return this.options.pagePatterns.some((pattern) => matchesUrlPattern(pageUrl, pattern));
    }

    private isInternalCaptureUrl(requestUrl: string): boolean {
        const captureUrl = toAbsoluteUrl(`${this.options.transport.getApiUrl()}/api/post-capture`);

        try {
            const incoming = new URL(requestUrl);
            const internal = new URL(captureUrl);
            return incoming.origin === internal.origin && incoming.pathname === internal.pathname;
        } catch {
            return requestUrl === captureUrl;
        }
    }

    private shouldCaptureRequest(method: string, requestUrl: string): boolean {
        if (normalizeMethod(method) !== 'POST') {
            return false;
        }

        if (this.isInternalCaptureUrl(requestUrl)) {
            return false;
        }

        if (!isSameSiteRequestUrl(requestUrl, this.options.siteDomain)) {
            return false;
        }

        return this.isPageMatched(getCurrentPageUrl());
    }

    private async dispatchCapture(
        source: PostRequestCaptureSource,
        requestUrl: string,
        contentType: string | undefined,
        serializedPayload: SerializedPayload,
        formContext?: {
            formAction?: string;
            formMethod?: string;
            formId?: string;
            formClass?: string;
        },
        options: { keepalive?: boolean } = {}
    ): Promise<void> {
        if (!this.isPageMatched(getCurrentPageUrl())) {
            return;
        }

        const event: PostRequestCaptureEvent = {
            siteName: this.options.siteDomain,
            sessionId: this.options.transport.getSessionId(),
            personId: getPersonId() ?? undefined,
            pageUrl: getCurrentPageUrl(),
            capturedAt: new Date().toISOString(),
            source,
            requestUrl,
            requestMethod: 'POST',
            contentType,
            payloadKind: serializedPayload.payloadKind,
            payloadJson: serializedPayload.payloadJson,
            payloadText: serializedPayload.payloadText,
            hasFiles: serializedPayload.hasFiles,
            filesMeta: serializedPayload.filesMeta,
            formAction: formContext?.formAction,
            formMethod: formContext?.formMethod,
            formId: formContext?.formId,
            formClass: formContext?.formClass,
        };

        try {
            await this.options.transport.capturePostRequest(event, {
                keepalive: options.keepalive,
                fetchImpl: this.originalFetch ?? undefined,
            });
        } catch (error) {
            logger.debug('POST capture: failed to send capture', {
                error: error instanceof Error ? error.message : String(error),
                source,
                requestUrl,
            });
        }
    }

    private patchFetch(): void {
        if (typeof window.fetch !== 'function') {
            return;
        }

        this.originalFetch = window.fetch;
        window.fetch = (async (input: RequestInfo | URL, init?: RequestInit): Promise<Response> => {
            const method = normalizeMethod(
                init?.method ?? (input instanceof Request ? input.method : undefined)
            );
            const requestUrl = toAbsoluteUrl(
                input instanceof Request ? input.url : input.toString()
            );
            const shouldCapture = this.shouldCaptureRequest(method, requestUrl);
            const keepalive =
                init?.keepalive ?? (input instanceof Request ? input.keepalive : undefined);
            const contentType = normalizeContentType(
                getHeaderValue(init?.headers, 'content-type') ??
                    (input instanceof Request
                        ? (input.headers.get('content-type') ?? undefined)
                        : undefined) ??
                    inferContentTypeFromBody(init?.body)
            );

            let requestClone: Request | null = null;
            if (shouldCapture && input instanceof Request && init?.body === undefined) {
                try {
                    requestClone = input.clone();
                } catch (error) {
                    logger.debug('POST capture: failed to clone fetch request', {
                        error: error instanceof Error ? error.message : String(error),
                        requestUrl,
                    });
                }
            }

            if (shouldCapture) {
                void (async () => {
                    const serializedPayload = maybeRedactSerializedPayload(
                        requestClone
                            ? await serializeRequestClone(requestClone, contentType)
                            : await serializeBody(init?.body, contentType),
                        this.options.redactPayloadValues
                    );

                    await this.dispatchCapture(
                        'fetch',
                        requestUrl,
                        contentType,
                        serializedPayload,
                        undefined,
                        {
                            keepalive,
                        }
                    );
                })();
            }

            const originalFetch = this.originalFetch;
            if (!originalFetch) {
                throw new Error('Original fetch is not available');
            }
            return originalFetch.call(window, input, init);
        }) as typeof window.fetch;
    }

    private getXhrMetadata(xhr: XMLHttpRequest): XhrCaptureMetadata {
        const metadata = (
            xhr as XMLHttpRequest & {
                [XHR_CAPTURE_METADATA]?: XhrCaptureMetadata;
            }
        )[XHR_CAPTURE_METADATA];

        if (metadata) {
            return metadata;
        }

        const initialMetadata: XhrCaptureMetadata = { headers: {} };
        (
            xhr as XMLHttpRequest & {
                [XHR_CAPTURE_METADATA]?: XhrCaptureMetadata;
            }
        )[XHR_CAPTURE_METADATA] = initialMetadata;
        return initialMetadata;
    }

    private patchXMLHttpRequest(): void {
        const manager = this;
        this.originalXhrOpen = XMLHttpRequest.prototype.open;
        this.originalXhrSetRequestHeader = XMLHttpRequest.prototype.setRequestHeader;
        this.originalXhrSend = XMLHttpRequest.prototype.send;

        XMLHttpRequest.prototype.open = function (
            this: XMLHttpRequest,
            method: string,
            url: string | URL,
            async?: boolean,
            username?: string | null,
            password?: string | null
        ): void {
            const metadata = manager.getXhrMetadata(this);
            if (metadata) {
                metadata.method = method;
                metadata.url = url.toString();
                metadata.headers = {};
            }

            manager.originalXhrOpen?.call(
                this,
                method,
                url,
                async ?? true,
                username ?? null,
                password ?? null
            );
        };

        XMLHttpRequest.prototype.setRequestHeader = function (
            this: XMLHttpRequest,
            header: string,
            value: string
        ): void {
            const metadata = manager.getXhrMetadata(this);
            if (metadata) {
                metadata.headers[header.toLowerCase()] = value;
            }

            manager.originalXhrSetRequestHeader?.call(this, header, value);
        };

        XMLHttpRequest.prototype.send = function (
            this: XMLHttpRequest,
            body?: Document | XMLHttpRequestBodyInit | null
        ): void {
            const metadata = manager.getXhrMetadata(this);
            const requestUrl = metadata?.url ? toAbsoluteUrl(metadata.url) : getCurrentPageUrl();
            const method = normalizeMethod(metadata?.method);

            if (metadata && manager.shouldCaptureRequest(method, requestUrl)) {
                const contentType = normalizeContentType(
                    metadata.headers['content-type'] ?? inferContentTypeFromBody(body)
                );

                void (async () => {
                    const serializedPayload = maybeRedactSerializedPayload(
                        await serializeBody(body, contentType),
                        manager.options.redactPayloadValues
                    );
                    await manager.dispatchCapture(
                        'xhr',
                        requestUrl,
                        contentType,
                        serializedPayload
                    );
                })();
            }

            manager.originalXhrSend?.call(this, body);
        };
    }

    private patchSendBeacon(): void {
        if (typeof navigator === 'undefined' || typeof navigator.sendBeacon !== 'function') {
            return;
        }

        this.originalSendBeacon = navigator.sendBeacon.bind(navigator);
        navigator.sendBeacon = (url: string | URL, data?: BodyInit | null): boolean => {
            const requestUrl = toAbsoluteUrl(url);

            if (this.shouldCaptureRequest('POST', requestUrl)) {
                const contentType = normalizeContentType(
                    (typeof Blob !== 'undefined' && data instanceof Blob && data.type) ||
                        inferContentTypeFromBody(data)
                );

                void (async () => {
                    const serializedPayload = maybeRedactSerializedPayload(
                        await serializeBody(data, contentType),
                        this.options.redactPayloadValues
                    );
                    await this.dispatchCapture(
                        'send_beacon',
                        requestUrl,
                        contentType,
                        serializedPayload,
                        undefined,
                        { keepalive: true }
                    );
                })();
            }

            if (!this.originalSendBeacon) {
                return false;
            }
            return this.originalSendBeacon(url, data);
        };
    }

    private handleSubmit = (event: Event): void => {
        const form = event.target;
        if (!(form instanceof HTMLFormElement)) {
            return;
        }

        const method = getFormMethod(form);
        const formAction = getFormAction(form);
        if (!this.shouldCaptureRequest(method, formAction)) {
            return;
        }

        const submitter =
            'submitter' in event ? ((event as SubmitEvent).submitter as HTMLElement | null) : null;
        queueMicrotask(() => {
            if (event.defaultPrevented) {
                return;
            }

            const contentType = normalizeContentType(
                form.enctype || 'application/x-www-form-urlencoded'
            );
            const formData = buildFormData(form, submitter);
            const serializedPayload = maybeRedactSerializedPayload(
                serializeFormPayload(formData, contentType),
                this.options.redactPayloadValues
            );

            void this.dispatchCapture(
                'native_form_submit',
                formAction,
                contentType,
                serializedPayload,
                {
                    formAction,
                    formMethod: method,
                    formId: form.id || undefined,
                    formClass: getFormClassName(form),
                },
                { keepalive: true }
            );
        });
    };

    private patchNativeForms(): void {
        const manager = this;
        document.addEventListener('submit', this.handleSubmit, true);

        this.originalFormSubmit = HTMLFormElement.prototype.submit;
        HTMLFormElement.prototype.submit = function (this: HTMLFormElement): void {
            const method = getFormMethod(this);
            const formAction = getFormAction(this);

            if (manager.shouldCaptureRequest(method, formAction)) {
                const contentType = normalizeContentType(
                    this.enctype || 'application/x-www-form-urlencoded'
                );
                const formData = buildFormData(this, null);
                const serializedPayload = maybeRedactSerializedPayload(
                    serializeFormPayload(formData, contentType),
                    manager.options.redactPayloadValues
                );

                void manager.dispatchCapture(
                    'native_form_direct_submit',
                    formAction,
                    contentType,
                    serializedPayload,
                    {
                        formAction,
                        formMethod: method,
                        formId: this.id || undefined,
                        formClass: getFormClassName(this),
                    },
                    { keepalive: true }
                );
            }

            manager.originalFormSubmit?.call(this);
        };
    }
}

let activePostRequestCaptureManager: PostRequestCaptureManager | null = null;

export function setupPostRequestCapture(options: PostRequestCaptureManagerOptions): void {
    if (!options.enabled) {
        cleanupPostRequestCapture();
        return;
    }

    if (!activePostRequestCaptureManager) {
        activePostRequestCaptureManager = new PostRequestCaptureManager(options);
        activePostRequestCaptureManager.start();
        return;
    }

    activePostRequestCaptureManager.update(options);
}

export function cleanupPostRequestCapture(): void {
    activePostRequestCaptureManager?.stop();
    activePostRequestCaptureManager = null;
}
